import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

interface FieldChangeEvent {
  fieldKey: string;
  value: string;
}

interface ReadingActionEvent {
  readingIndex: number;
}

@Component({
  selector: 'app-job-request-test-electrical-stability-detail',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  templateUrl: './job-request-test-electrical-stability-detail.component.html',
  styleUrl: './job-request-test-electrical-stability-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobRequestTestElectricalStabilityDetailComponent {
  private static readonly DEFAULT_TEMPERATURE_F = 120;
  private visibleReadingIndices: number[] = [];
  private pendingAddBaseIndices: number[] | null = null;
  private lastTestKey = '';

  @Input({ required: true }) testKey = '';
  @Input({ required: true }) fieldValueByKey!: (testKey: string, fieldKey: string) => string;
  @Input({ required: true }) computedValueByKey!: (testKey: string, fieldKey: string) => string;
  @Input({ required: true }) readingIndicesByKey!: (testKey: string) => number[];
  @Input() showAllStoredReadingsByKey: (testKey: string) => boolean = () => false;

  @Output() fieldChanged = new EventEmitter<FieldChangeEvent>();
  @Output() addReading = new EventEmitter<void>();
  @Output() removeReading = new EventEmitter<ReadingActionEvent>();

  protected onFieldInput(fieldKey: string, value: string): void {
    this.fieldChanged.emit({ fieldKey, value });
  }

  protected onAddReading(): void {
    const existingIndices = [...this.readingIndicesByKey(this.testKey)];
    this.visibleReadingIndices = this.displayedReadingIndices();
    this.pendingAddBaseIndices = existingIndices;
    this.addReading.emit();
  }

  protected onRemoveReading(readingIndex: number): void {
    this.removeReading.emit({ readingIndex });
  }

  protected readingTemperatureKey(readingIndex: number): string {
    return `esReading${readingIndex}Temperature`;
  }

  protected readingVoltageKey(readingIndex: number, voltageIndex: 1 | 2 | 3): string {
    return `esReading${readingIndex}Voltage${voltageIndex}`;
  }

  protected readingAverageKey(readingIndex: number): string {
    return `esReading${readingIndex}AverageVoltage`;
  }

  protected displayedReadingIndices(): number[] {
    const readingIndices = this.readingIndicesByKey(this.testKey);
    if (!readingIndices?.length) {
      this.visibleReadingIndices = [];
      return [];
    }

    if (this.lastTestKey !== this.testKey) {
      this.lastTestKey = this.testKey;
      this.visibleReadingIndices = [];
      this.pendingAddBaseIndices = null;
    }

    if (this.visibleReadingIndices.length === 0) {
      const shouldShowAllStored = this.showAllStoredReadingsByKey(this.testKey);
      if (shouldShowAllStored) {
        this.visibleReadingIndices = [...readingIndices];
        return readingIndices;
      }

      const defaultReading = readingIndices.find((readingIndex) => {
        const rawTemperature = this.fieldValueByKey(this.testKey, this.readingTemperatureKey(readingIndex));
        return this.parseTemperature(rawTemperature) === JobRequestTestElectricalStabilityDetailComponent.DEFAULT_TEMPERATURE_F;
      });

      this.visibleReadingIndices = [defaultReading ?? readingIndices[0]];
    }

    if (this.pendingAddBaseIndices) {
      const newlyAddedIndex = readingIndices.find((readingIndex) => !this.pendingAddBaseIndices!.includes(readingIndex));
      if (newlyAddedIndex != null && !this.visibleReadingIndices.includes(newlyAddedIndex)) {
        this.visibleReadingIndices = [...this.visibleReadingIndices, newlyAddedIndex];
      }

      this.pendingAddBaseIndices = null;
    }

    this.visibleReadingIndices = this.visibleReadingIndices.filter((readingIndex) => readingIndices.includes(readingIndex));

    return readingIndices.filter((readingIndex) => this.visibleReadingIndices.includes(readingIndex));
  }

  private parseTemperature(value: string | null | undefined): number | null {
    if (value == null || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
